import '../SettingsPage.scss';
import { Button, Checkbox, Icon, Input } from '@design-system/index';
import { ArchiveFilled, ArchiveLine, ModifierLine, ModifierFull, UnarchiveLine, UnarchiveFilled } from '@icons';
import { MutationTable } from '@components/Table/MutationTable/MutationTable';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useETicketTypesPageVM } from './useETicketTypesPageVM';
import { Formik } from 'formik';
import { ETicketTypeToCreate } from '@services/product/eTicketType/e-ticket-type.types';
import { ETicketType } from '@services/product/eTicketType/e-ticket-type.types';

export default function ETicketTypesPage() {
    const {
        mutationHook,
        isArchived,
        handleToggleArchiveETicketTypes,
        handleToggleArchiveETicketType,
        updateETicketType,
        createETicketType,
        MySwal,
        needReload,
        setIsArchived
    } = useETicketTypesPageVM();
    const columnHelper = createColumnHelper<ETicketType>();

    const handleModificationETicketTypes = async (eticketType: ETicketTypeToCreate | null, id?: number) => {
        await MySwal.fire<ETicketTypeToCreate>({
            title: eticketType ? "Modification d'un type de e-billet" : "Création d'un type de e-billet",
            showCancelButton: true,
            confirmButtonColor: '#5D4B99',
            confirmButtonText: eticketType ? 'Modifier' : 'Créer',
            cancelButtonColor: '#FF5F46',
            cancelButtonText: 'Annuler',
            html: (
                <Formik
                    initialValues={{ label: eticketType?.label ?? '' }}
                    onSubmit={(values) => {
                        if (id) {
                            updateETicketType({
                                id: id.toString(),
                                eticketType: {
                                    label: values.label
                                }
                            });
                        } else {
                            createETicketType({
                                label: values.label
                            });
                        }
                        MySwal.close();
                    }}
                >
                    {({ values, handleChange, handleSubmit }) => (
                        <form id="form-eticket-type" onSubmit={handleSubmit}>
                            <Input name="label" label="Libellé" placeholder="Libellé" value={values.label} required onChange={handleChange} />
                        </form>
                    )}
                </Formik>
            ),
            focusConfirm: false,
            preConfirm: () => {
                const form = document.getElementById('form-eticket-type') as HTMLFormElement;
                form.requestSubmit();
                return false;
            }
        });
    };

    const columns = [
        columnHelper.display({
            id: 'select',
            size: 0,
            cell: ({ row }) => (
                <Checkbox
                    checked={row.getIsSelected()}
                    onChange={row.getToggleSelectedHandler()}
                    aria-label={`Sélectionner ligne ${row.id}`}
                />
            )
        }),
        columnHelper.accessor('label', {
            header: 'Libellé',
            enableSorting: true,
            cell: (info) => {
                return <>{info.getValue()}</>;
            }
        }),
        columnHelper.accessor('availableStock', {
            header: 'Stock disponible',
            enableSorting: false,
            cell: (info) => {
                return <>{info.getValue()}</>;
            }
        }),
        columnHelper.display({
            header: 'Actions',
            cell: (info) => {
                return (
                    <div className="settings-actions">
                        <Button
                            variant="icon"
                            color="primary"
                            size="small"
                            hoverIcon={<Icon icon={<ModifierFull />} size={20} color="primary" />}
                            icon={<Icon icon={<ModifierLine />} size={20} color="primary" />}
                            onClick={() => {
                                handleModificationETicketTypes({label: info.row.original.label }, info.row.original.id);
                            }}
                        />
                        <Button
                            variant="icon"
                            color="primary"
                            size="small"
                            hoverIcon={<Icon icon={isArchived ? <UnarchiveFilled /> : <ArchiveFilled />} size={20} color="primary" />}
                            icon={<Icon icon={isArchived ? <UnarchiveLine /> : <ArchiveLine />} size={20} color="primary" />}
                            onClick={() => handleToggleArchiveETicketType(info.row.original.id, !isArchived)}
                        />
                    </div>
                );
            }
        })
    ] as ColumnDef<ETicketType>[];

    return (
        <>
            <div className="settings-tabs-content service-codes">
                <MutationTable
                    noHeader
                    key={isArchived ? 'archived' : 'active'}
                    columns={columns}
                    mutationHook={mutationHook}
                    defaultSorting={{
                        field: 'label',
                        direction: 'desc'
                    }}
                    needReload={needReload}
                    createButtonOnClick={() => handleModificationETicketTypes(null)}
                    createButtonLabel="Créer un type de e-billet"
                    archiveButtonOnClick={() => setIsArchived(!isArchived)}
                    archiveButtonLabel={isArchived ? 'Actifs' : 'Archivés'}
                    deleting={{
                        enabled: isArchived,
                        entity: 'eticket_type',
                    }}
                    footer={{
                        actions: [
                            {
                                label: isArchived ? 'Désarchiver' : "Archiver",
                                icon: <Icon icon={isArchived ? <UnarchiveLine /> : <ArchiveLine />} size={20} color="primary" />,
                                hoverIcon: <Icon icon={isArchived ? <UnarchiveFilled /> : <ArchiveFilled />} size={20} color="primary" />,
                                onClick: (lines: ETicketType[]) => handleToggleArchiveETicketTypes(lines, !isArchived)
                            }
                        ]
                    }}
                />
            </div>
        </>
    );
}
